import React, { useState } from "react";
import { DialogLayout } from "subframe/layouts/DialogLayout";
import { Calendar } from "subframe/components/Calendar";
import { Button } from "subframe/components/Button";
import * as SubframeCore from "@subframe/core";
import { useMutation } from "@tanstack/react-query";
import { createEscalationReminder } from "modules/Retention/services/retention";
import { notify } from "modules/Retention/providers/NotificationProvider";
import { AlertVariant } from "modules/Retention/types";

interface SetReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: SetReminderData) => void;
  escalationId?: number;
  employeeId?: number;
}

interface SetReminderData {
  reminderDateTime: string;
  reminderEscalationId: number;
}

export const SetReminderDialog: React.FC<SetReminderDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  escalationId,
}) => {
  const [reminderDate, setReminderDate] = useState(new Date().toISOString());
  const [reminderTime, setReminderTime] = useState("10:00");

  // Function to create reminder datetime string in ISO format
  const createReminderDateTime = (date: string, time: string): string => {
    const dateObj = new Date(date);
    const [hours, minutes] = time.split(':');
    dateObj.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return dateObj.toISOString();
  };

  // Create reminder mutation
  const reminderMutation = useMutation({
    mutationFn: async (payload: any) => {
      return await createEscalationReminder(payload);
    },
    onSuccess: (response) => {
      const reminderDateTime = createReminderDateTime(reminderDate, reminderTime);
      
      notify({
        title: "Reminder set successfully",
        description: `Reminder has been set for ${new Date(reminderDateTime).toLocaleDateString()} at ${new Date(reminderDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
        variant: AlertVariant.SUCCESS
      });
      
      if (onSubmit && response?.data?.reminder_escalation_id) {
        onSubmit({
          reminderDateTime,
          reminderEscalationId: response.data.reminder_escalation_id,
        });
      }
      handleClose();
    },
    onError: (error: any) => {
      notify({
        title: "Failed to set reminder",
        description: error?.message || "An error occurred while setting the reminder. Please try again.",
        variant: AlertVariant.ERROR
      });
    },
  });

  const handleSubmit = () => {
    if (!escalationId) {
      notify({
        title: "Missing required information",
        description: "Escalation ID is required to set a reminder.",
        variant: AlertVariant.ERROR
      });
      return;
    }

    const reminderDateTime = createReminderDateTime(reminderDate, reminderTime);
    
    // Check if reminder is in the future
    if (new Date(reminderDateTime) <= new Date()) {
      notify({
        title: "Invalid reminder time",
        description: "Please select a future date and time for the reminder.",
        variant: AlertVariant.ERROR
      });
      return;
    }

    const payload = {
      escalation_id: escalationId,
      reminder_datetime: reminderDateTime,
      // Will use current user by default since no email/ID provided
    };

    reminderMutation.mutate(payload);
  };

  const handleClose = () => {
    setReminderDate(new Date().toISOString());
    setReminderTime("10:00");
    onOpenChange(false);
  };

  return (
    <div className="z-30">
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex h-full w-112 flex-col items-start gap-6 rounded-lg bg-default-background px-6 py-6">
          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex items-center gap-2">
              <SubframeCore.Icon
                className="text-section-header font-section-header text-default-font"
                name="FeatherCalendar"
              />
              <span className="text-section-header font-section-header text-default-font">
                Set Reminder
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex flex-col lg:flex-row items-start gap-3 sm:gap-4 w-full">
                <div className="flex flex-col items-start gap-2 w-full lg:w-auto">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Reminder Date*
                  </span>
                  <div className="w-full lg:w-auto">
                    <SubframeCore.Popover.Root>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          variant="neutral-secondary"
                          icon="FeatherCalendar"
                          className="w-full lg:w-auto"
                        >
                          {new Date(reminderDate).toLocaleDateString()}
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Portal>
                        <SubframeCore.Popover.Content
                          side="bottom"
                          align="start"
                          sideOffset={4}
                          className="z-[60]"
                        >
                          <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg">
                            <Calendar
                              mode="single"
                              selected={new Date(reminderDate)}
                              onSelect={(date: Date | undefined) => {
                                if (date) {
                                  setReminderDate(date.toISOString());
                                }
                              }}
                              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            />
                          </div>
                        </SubframeCore.Popover.Content>
                      </SubframeCore.Popover.Portal>
                    </SubframeCore.Popover.Root>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2 w-full lg:w-auto">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Reminder Time*
                  </span>
                  <input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="h-8 w-full lg:w-auto px-3 py-2 border border-solid border-neutral-border rounded-md bg-default-background text-body font-body text-default-font focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex w-full flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  You will receive a notification at the specified date and time to follow up on this escalation.
                </span>
              </div>
            </div>
          </div>
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-secondary"
              onClick={handleClose}
              disabled={reminderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={reminderMutation.isPending}
              loading={reminderMutation.isPending}
            >
              Set Reminder
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
}; 